import { HttpResponse } from '../../interfaces/api/http';
import http from '../http';
import api from '../../config/api';
import { CoursesListResponse, CourseDetailsResponse } from '../../interfaces/api/courses';

/**
 * Courses service module.
 * Provides methods to interact with courses API for fetching course data and performing actions.
 */
const courses = {
  /**
   * Fetches a paginated list of courses.
   *
   * @param {number} page - The page number for the courses list.
   * @returns {Promise<HttpResponse<CoursesListResponse>>} - A promise resolving to the list of courses.
   *
   * @example
   * courses.getList(1).then((response) => {
   *   console.log(response.data);
   * }).catch((error) => {
   *   console.error(error);
   * });
   */
  getList: (page: number): Promise<HttpResponse<CoursesListResponse>> => {
    return http.get<CoursesListResponse>(`${api.courses}?page=${page}`);
  },

  /**
   * Fetches detailed information about a specific course.
   *
   * @param {number} id - The ID of the course.
   * @returns {Promise<HttpResponse<CourseDetailsResponse>>} - A promise resolving to the course details.
   *
   * @example
   * courses.getDetails(123).then((response) => {
   *   console.log(response.data);
   * }).catch((error) => {
   *   console.error(error);
   * });
   */
  getDetails: (id: number): Promise<HttpResponse<CourseDetailsResponse>> => {
    return http.get<CourseDetailsResponse>(`${api.courses}/${id}`);
  },

  /**
   * Submits a video as completed for a specific course.
   *
   * @param {number} id - The ID of the video to mark as completed.
   * @returns {Promise<HttpResponse<null>>} - A promise resolving to the response of the submission.
   *
   * @example
   * courses.submitVideo(456).then(() => {
   *   console.log('Video submitted successfully');
   * }).catch((error) => {
   *   console.error(error);
   * });
   */
  submitVideo: (id: number): Promise<HttpResponse<null>> => {
    return http.post<null>(`${api.submitVideo}/${id}`, {});
  },

  /**
   * Enrolls a user in a specific course.
   *
   * @param {number} id - The ID of the course to enroll in.
   * @returns {Promise<HttpResponse<null>>} - A promise resolving to the response of the enrollment.
   *
   * @example
   * courses.enrollInCourse(789).then(() => {
   *   console.log('Enrolled in course successfully');
   * }).catch((error) => {
   *   console.error(error);
   * });
   */
  enrollInCourse: (id: number): Promise<HttpResponse<null>> => {
    return http.post<null>(`${api.enrollInCourse}/${id}`, {});
  },
};

export default courses;
